<script setup lang="ts">
import { DeleteConfirmationModal } from '@/components/common/DeleteConfirmationModal'
import { currencyFormatter } from '@/helpers/ep/formatters'
import type { IISORealTimeLMPThresholdForm } from '@/types/eos/alerts/forms'
import ViewLayout from '@/views/layouts/ViewLayout.vue'
import { Icon } from '@iconify/vue'
import { type FormInstance, type FormRules } from 'element-plus'
import { computed, reactive, ref } from 'vue'
import { ISOs } from '@/types/core/Site'
import { useRealTimeLMPThresholds } from '@/composables/eos/real-time-lmp-thresholds'

const dialogVisible = ref<boolean>(false)

enum FormMode {
  CREATE = 'Create',
  UPDATE = 'Update',
}

const formMode = ref<FormMode>()
const formRef = ref<FormInstance>()
const baseForm = {
  id: undefined,
  iso: undefined,
  node: undefined,
  lmp_threshold_value_per_mwh: undefined,
  slack_notification_flag: false,
  slack_channel_webhook_url: null,
}
const form = reactive<IISORealTimeLMPThresholdForm>({
  id: undefined,
  iso: undefined,
  node: undefined,
  lmp_threshold_value_per_mwh: undefined,
  slack_notification_flag: false,
  slack_channel_webhook_url: null,
})

const formRules = reactive<FormRules<IISORealTimeLMPThresholdForm>>({
  iso: [{ required: true, message: 'ISO is required.', trigger: ['blur'] }],
  node: [{ required: true, message: 'Node is required.', trigger: ['blur'] }],
  lmp_threshold_value_per_mwh: [{ required: true, message: 'LMP threshold value is required.', trigger: ['blur'] }],
  slack_channel_webhook_url: [
    {
      type: 'string',
      validator: (rule: any, value: any, callback: any) => {
        if (form.slack_notification_flag && !value) {
          callback(new Error('Please provide a Slack Channel Webhook URL.'))
        }
        callback()
      },
    },
  ],
})

/**
 * Opens create/update LMP Alert dialog.
 *
 * Not necessarily thrilled with this implementation,
 * but it's an Element Plus workaround more than anything.
 *
 * Will be fixed once this view is broken down into multiple components.
 *
 * @param formEl form instance.
 * @param index index in table of item to open. Note: this is a string as `if (0)` is `false`!
 */
const openDialog = (formEl: FormInstance | undefined, rowIndex?: string) => {
  if (rowIndex) {
    formMode.value = FormMode.UPDATE
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { created_at, updated_at, ...target } = data.value![+rowIndex] // remove created and updated at fields
    Object.assign(form, target)
  } else {
    formMode.value = FormMode.CREATE
  }
  dialogVisible.value = true
}

const {
  getRealTimeLMPThresholdsQuery,
  createRealTimeLMPThresholdMutation,
  updateRealTimeLMPThresholdMutation,
  deleteRealTimeLMPThresholdMutation,
} = useRealTimeLMPThresholds()

/**
 * Handles form submission.
 *
 * @param formEl alert form instance.
 */
const handleSubmit = async (formEl: FormInstance | undefined) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  await formEl?.validate(async (valid, fields) => {
    if (!valid) return

    // If "Send Slack notification" is unchecked, don't include the channel.
    if (!form.slack_notification_flag) {
      form.slack_channel_webhook_url = null
    }

    try {
      if (form.id) {
        await updateAsync(form)
      } else {
        await createAsync(form)
      }
    } catch {
      // Handled in composable
      return
    }

    dialogVisible.value = false
  })
}

// Vue Query
const { isFetching, isError, data, error, refetch } = getRealTimeLMPThresholdsQuery()

// Vue Query Create Mutation
const { isPending: isCreating, mutateAsync: createAsync, reset: resetCreate } = createRealTimeLMPThresholdMutation

// Vue Query Update Mutation
const { isPending: isUpdating, mutateAsync: updateAsync, reset: resetUpdate } = updateRealTimeLMPThresholdMutation

// Vue Query Delete Mutation
const { mutateAsync: deleteAsync } = deleteRealTimeLMPThresholdMutation

/**
 * Handles dialog close: resets form, clears mutation errors.
 */
const handleClosed = () => {
  resetUpdate()
  resetCreate()
  formRef.value?.resetFields()
  Object.assign(form, baseForm)
}

// For disabling of form on submit
const formSubmitting = computed<boolean>(() => {
  return isCreating.value || isUpdating.value
})
</script>

<template>
  <ViewLayout>
    <template v-if="!isError" #controls>
      <el-button type="primary" :disabled="isFetching" @click="openDialog(formRef)">Add LMP Threshold</el-button>
    </template>
    <rl-error-result v-if="isError" :error="error">
      <el-button @click="() => refetch()">
        <Icon icon="material-symbols:refresh-rounded" height="16" width="16" :inline="true" />
        &nbsp;Reload
      </el-button>
    </rl-error-result>
    <template v-else>
      <el-dialog
        v-model="dialogVisible"
        :title="`${formMode} LMP Threshold`"
        :width="520"
        :show-close="false"
        @closed="handleClosed"
      >
        <el-form
          :key="form.id"
          ref="formRef"
          label-position="left"
          :label-width="220"
          :model="form"
          :rules="formRules"
          :disabled="formSubmitting"
          hide-required-asterisk
        >
          <el-form-item prop="iso" label="ISO" required>
            <el-select v-model="form.iso" style="width: 100%" placeholder="Select ISO" clearable>
              <el-option v-for="iso in [ISOs.MISO]" :key="iso" :label="iso" :value="iso" />
            </el-select>
          </el-form-item>
          <el-form-item prop="node" label="Node" required>
            <el-input v-model="form.node" placeholder="Node" clearable />
          </el-form-item>
          <el-form-item label="LMP Threshold ($/MWh)" prop="lmp_threshold_value_per_mwh" required>
            <el-currency-input v-model="form.lmp_threshold_value_per_mwh" clearable />
          </el-form-item>
          <el-form-item prop="slack_notification_flag">
            <el-checkbox v-model="form.slack_notification_flag" label="Send Slack notification?" />
          </el-form-item>
          <el-collapse-transition>
            <div v-show="form.slack_notification_flag">
              <el-form-item
                prop="slack_channel_webhook_url"
                label="Slack Channel Webhook URL"
                :required="form.slack_notification_flag"
              >
                <el-input v-model="form.slack_channel_webhook_url" placeholder="Slack Channel Webhook URL" clearable />
              </el-form-item>
            </div>
          </el-collapse-transition>
        </el-form>
        <template #footer>
          <el-button @click="dialogVisible = false">Cancel</el-button>
          <el-button type="primary" :loading="formSubmitting" @click="handleSubmit(formRef)">Save</el-button>
        </template>
      </el-dialog>
      <rl-error-result v-if="isError" :error="error" />
      <el-table
        v-else
        v-loading="isFetching"
        :data="data"
        :empty-text="isFetching ? 'Loading...' : 'No thresholds found.'"
        :stripe="true"
      >
        <el-table-column label="ISO" prop="iso" :min-width="192" />
        <el-table-column label="Commercial Pricing Node" prop="node" :min-width="192" />
        <el-table-column
          label="LMP Threshold ($/MWh)"
          prop="lmp_threshold_value_per_mwh"
          :min-width="192"
          :formatter="currencyFormatter"
        >
        </el-table-column>
        <el-table-column label="Slack Alerts" prop="slack_notification_flag" :min-width="80">
          <template #default="scope">
            <el-checkbox v-model="scope.row.slack_notification_flag" disabled />
          </template>
        </el-table-column>
        <el-table-column :min-width="80" align="right">
          <template #default="scope">
            <el-button link @click="openDialog(formRef, scope.$index.toString())">
              <Icon icon="material-symbols:edit-outline" :height="20" />
            </el-button>
            <el-button link>
              <Icon
                icon="material-symbols:delete-outline"
                :height="20"
                @click="
                  DeleteConfirmationModal(
                    'Delete LMP Threshold',
                    'Are you sure you want to delete this threshold? This action cannot be undone',
                    scope.row.id,
                    deleteAsync
                  )
                "
              />
            </el-button>
          </template>
        </el-table-column>
      </el-table>
    </template>
  </ViewLayout>
</template>

<style scoped lang="scss">
.el-form {
  .el-form-item {
    margin-bottom: 0;
  }

  > div .el-form-item,
  > .el-form-item:not(:first-child) {
    padding-top: var(--spacing-spacing-lg);
  }
}
</style>
